<template>
  <div>
    <!-- Key Metrics Row -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
      <div class="bg-white border rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-500">Actual Balance</p>
            <p class="text-xl font-bold text-gray-800">GMD {{ formatPrice(data.actualbalance) }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white border rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-500">Total Sales</p>
            <p class="text-xl font-bold text-gray-800">GMD {{ data.total_sales ? formatPrice(data.total_sales.total / 100) : '0.00' }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white border rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100 text-purple-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-500">Total Transactions</p>
            <p class="text-xl font-bold text-gray-800">{{ data.total_sales ? data.total_sales.count : 0 }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white border rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-500">Total Commission</p>
            <p class="text-xl font-bold text-gray-800">GMD {{ formatPrice(data.totalcommission || 0) }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Agent Info & Accounts Row -->
    <div class="grid grid-cols-1 md:grid-cols-1 gap-4 mt-6">
 

      <!-- Accounts & Users Column -->
      <div class="space-y-4">
        <!-- Accounts Card -->
        <div class="bg-white border rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
            </svg>
            Accounts ({{ data.accounts ? data.accounts.length : 0 }})
          </h2>
          <div v-if="data.accounts && data.accounts.length > 0" class="space-y-3">
            <div 
              v-for="account in data.accounts" 
              :key="account.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <p class="text-sm font-medium text-gray-800">Account #{{ account.account_id }}</p>
                <p class="text-xs text-gray-500">Last updated: {{ account.updated_at | moment('DD MMM YYYY HH:mm') }}</p>
              </div>
              <div class="text-right">
                <p class="text-lg font-bold text-green-600">GMD {{ formatPrice(account.balance) }}</p>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 text-gray-500 text-sm">
            No accounts found
          </div>
        </div>

        <!-- Users Card -->
        <div class="bg-white border rounded-lg p-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <svg class="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
            Users ({{ data.users ? data.users.length : 0 }})
          </h2>
          <div v-if="data.users && data.users.length > 0" class="space-y-3">
            <div 
              v-for="user in data.users" 
              :key="user.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center">
                <div class="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                  {{ getInitials(user.name) }}
                </div>
                <div class="ml-3">
                  <p class="text-sm font-medium text-gray-800">{{ user.name }}</p>
                  <p class="text-xs text-gray-500">Joined: {{ user.created_at | moment('DD MMM YYYY') }}</p>
                </div>
              </div>
              <span 
                class="px-2 py-1 text-xs font-semibold rounded-full capitalize"
                :class="getUserTypeClass(user.type)"
              >
                {{ user.type }}
              </span>
            </div>
          </div>
          <div v-else class="text-center py-4 text-gray-500 text-sm">
            No users found
          </div>
        </div>
      </div>
    </div>

    <!-- Chart Section -->
    <div class="border p-4 rounded mt-6" v-if="data.stats">
      <staff-sales :stats="data.stats" />
    </div>
  </div>
</template>

<script>
import StaffSales from "@/components/Charts/StaffSales.vue";
import FormatPrice from "@/mixins/FormatPrice";

export default {
  props: ["data"],
  components: { StaffSales },

  mixins: [FormatPrice],

  data: () => ({
    isModalVisible: false,
    isLoading: false,
  }),

  methods: {
    showConfirmModal() {
      this.isModalVisible = true;
    },
    closeConfirmModal() {
      this.isModalVisible = false;
      this.$emit("complete");
    },

    confirmRequest(req) {
      console.log("User has confirmed", req);
      this.userHasConfirmed = req;

      if (this.userHasConfirmed == "yes") {
        // this.onSubmit();
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    getUserTypeClass(type) {
      const classes = {
        admin: 'bg-purple-100 text-purple-800',
        manager: 'bg-blue-100 text-blue-800',
        staff: 'bg-green-100 text-green-800',
        user: 'bg-gray-100 text-gray-800',
      };
      return classes[type] || 'bg-gray-100 text-gray-800';
    },
  },
};
</script>
